import { h, Component } from 'preact'

import translations from '../../../libs/translationsUtils/translations'
import Analytics from '../../analytics/Analytics'
import Icon from '../../assets'
import { SearchQuery } from './Search'

const eventTracker = () => {
  Analytics.send('Homepage', 'Search clicked')
}

const doSearch = (searchValue: string) => {
  if (!searchValue) return
  window.location.href = `/search?q=${searchValue}`
}

const getSearchQueryFromInput = (searchString: string) => {
  const searchQuery: SearchQuery = { q: searchString }
  const eventUrlPattern = /https?:\/\/.+\/events\/.+-(\d+)$/
  const eventUrl = eventUrlPattern.exec(searchString)
  const productUpdateUrlPattern = /https?:\/\/.+\/product-updates\/(\d+)$/
  const productUpdateUrl = productUpdateUrlPattern.exec(searchString)
  const ideaUrlPattern = /https?:\/\/.+\/ideas\/.+-(\d+)$/
  const ideaUrl = ideaUrlPattern.exec(searchString)

  if (eventUrl !== null) {
    searchQuery.q = eventUrl[1]
    searchQuery.contentType = 'event'
  }

  if (productUpdateUrl !== null) {
    searchQuery.q = productUpdateUrl[1]
    searchQuery.contentType = 'productUpdate'
  }

  if (ideaUrl !== null) {
    searchQuery.q = ideaUrl[1]
    searchQuery.contentType = 'ideation'
  }

  const m = /https?:\/\/.+\/.+-\d+\/.+-(\d+)$/.exec(searchString)
  if (m !== null) {
    searchQuery.q = m[1]
  }

  return searchQuery
}

interface Props {
  reset?: () => void
  clearSelection?: () => void
  refine?: (query: string) => void
  setHighlightedIndex?: (index: number) => void
  inputValue?: string
  getInputProps?: (attrs: any) => object // should match what Downshift wants to have here
  openMenu?: () => void
  isOpen?: boolean
  isControl?: boolean
  placeholder?: string
  focusOnMount?: boolean
  enableFocusToSearch?: boolean
  updateSearch?: (query: SearchQuery, cb: () => void) => void
  submitIconType?: string
}

const noop = () => ({})

const submitIcon = (type: string) => {
  switch (type) {
    case 'add':
      return (
        <Icon icon='add' width={16} height={16} color='currentColor' />
      )
    default:
      return (
        <Icon icon='search' width={16} height={16} color='currentColor' />
      )
  }
}

class SearchBox extends Component<Props> {
  input?: HTMLInputElement
  setRef = (input: HTMLInputElement) => { this.input = input }

  componentDidMount () {
    if (!this.input) return
    if (!this.props.focusOnMount) return
    this.input.focus()
    setTimeout(() => {
      if (this.input) {
        this.input.focus()
      }
    }, 100)
  }

  componentDidUpdate (previousProps: Readonly<Props>) {
    if (previousProps.enableFocusToSearch !== this.props.enableFocusToSearch && this.props.enableFocusToSearch && this.input) {
      this.input.focus()
      setTimeout(() => {
        if (this.input) {
          this.input.focus()
        }
      }, 100)
    }
  }

  render ({
    reset = noop,
    clearSelection = noop,
    refine = noop,
    setHighlightedIndex = noop,
    inputValue = '',
    getInputProps = noop,
    openMenu = noop,
    isOpen = false,
    isControl = false,
    placeholder = '',
    updateSearch = noop,
    submitIconType
  }: Props) {
    return (
      <div class='search-box'>
        <input
          ref={this.setRef}
          aria-label='Search'
          type='search'
          placeholder={isControl ? placeholder : translations.translate('Forum', 'live.search.placeholder')}
          autocomplete='off'
          autocorrect='off'
          autocapitalize='off'
          className={`search-box__input ${inputValue && isOpen ? 'search-box__input--focused' : ''}`}
          {...getInputProps({
            onChange (e: {target: HTMLInputElement}) {
              const searchQuery = getSearchQueryFromInput(e.target.value)
              updateSearch(searchQuery, () => {
                refine(searchQuery.q)
                setHighlightedIndex(-1)
              })
            },
            onKeyDown (e: KeyboardEvent) {
              if (e.keyCode === 13) {
                e.preventDefault()
                doSearch(inputValue)
              }
            },
            onSubmit (e: Event) {
              e.preventDefault()
              doSearch(inputValue)
            },
            onFocus: () => {
              eventTracker()
              openMenu()
            }
          })}
        />
        <button type='submit' aria-label='Enter' class='search-box__submit' onClick={(e) => { e.preventDefault(); doSearch(inputValue) }}>
          {submitIcon(submitIconType ?? '')}
        </button>
        <button
          type='reset'
          aria-label='Clear search'
          class='search-box__reset'
          onClick={() => {
            clearSelection()
            reset()
            this.input?.focus()
          }} style={{ opacity: inputValue ? 1 : 0 }}
        >
          <Icon icon='cancel' width={16} height={16} color='currentColor' />
        </button>
      </div>
    )
  }
}

export { SearchBox, doSearch }
